// Centralized AskPurple mascot emotion catalog.
// 15 user-requested states + legacy aliases for backwards-compat with both
// the floating widget and the full-page chat.

// Base sprite files (already cropped/centered to 512x512 squares)
const SLEEPING = "/askpurple-emotions/sleeping.png";
const HAPPY = "/askpurple-emotions/happy.png";
const THINKING = "/askpurple-emotions/thinking.png";
const SURPRISED = "/askpurple-emotions/surprised.png";
const STUDYING = "/askpurple-emotions/studying.png";
const LISTENING = "/askpurple-emotions/listening.png";
const TYPING = "/askpurple-emotions/typing.png";
const CELEBRATING_ADMISSION = "/askpurple-emotions/celebrating_admission.png";
const TRAVEL = "/askpurple-emotions/travel.png";
const TUTOR = "/askpurple-emotions/tutor.png";

// 15 canonical mascot states the user explicitly asked for.
export const EMOTIONS = {
  sleeping: SLEEPING,
  happy: HAPPY,
  thinking: THINKING,
  surprised: SURPRISED,
  cheering: HAPPY,
  confused: THINKING,
  studying: STUDYING,
  typing: TYPING,
  listening: LISTENING,
  celebrating_admission: CELEBRATING_ADMISSION,
  travel: TRAVEL,
  tutor: TUTOR,
  counsellor: LISTENING,
  ai_search: STUDYING,
  waiting: THINKING,

  // Legacy aliases used in older code paths
  idle: HAPPY,
  reply: HAPPY,
  greet: HAPPY,
  wow: SURPRISED,
  wink: HAPPY,
  idea: HAPPY,
  searching: STUDYING,
  reading: STUDYING,
  talking: LISTENING,
};

export const REACTIONS = {
  sleeping: "💤",
  happy: "😊",
  thinking: "💭",
  surprised: "🤩",
  cheering: "🎉",
  confused: "🤔",
  studying: "📚",
  typing: "⌨️",
  listening: "🎧",
  celebrating_admission: "🎓",
  travel: "✈️",
  tutor: "👩‍🏫",
  counsellor: "🪄",
  ai_search: "🔍",
  waiting: "⏳",

  idle: "😊",
  reply: "✨",
  greet: "👋",
  wow: "🤩",
  wink: "😉",
  idea: "💡",
  searching: "🔍",
  reading: "📖",
  talking: "💬",
};

export const REACTION_CAPTIONS = {
  sleeping: "Zzz... resting",
  happy: "Love it!",
  thinking: "Hmm, thinking...",
  surprised: "Whoa, really?",
  cheering: "You got this!",
  confused: "Wait, what?",
  studying: "Let's study",
  typing: "Typing it up...",
  listening: "I'm all ears",
  celebrating_admission: "Admission unlocked!",
  travel: "Pack your bags!",
  tutor: "Class is in!",
  counsellor: "Counsellor mode",
  ai_search: "Searching with AI",
  waiting: "One sec...",

  idle: "All ears!",
  reply: "Here's the deal",
  greet: "Hey there!",
  wow: "Whoa, nice!",
  wink: "Got it 😉",
  idea: "Idea incoming!",
  searching: "Searching for you",
  reading: "Reading carefully",
  talking: "Here's the deal",
};

// Per-state float / wobble animation
export const MASCOT_ANIMATIONS = {
  sleeping: { y: [0, 1, 0] },
  happy: { y: [0, -3, 0], rotate: [0, -2, 2, 0] },
  thinking: { rotate: [-5, 5, -5], y: [0, -2, 0] },
  surprised: { scale: [1, 1.1, 1], y: [0, -5, 0] },
  cheering: { y: [0, -5, 0], rotate: [0, -6, 6, 0] },
  confused: { rotate: [-3, 3, -3] },
  studying: { y: [0, -2, 0] },
  typing: { x: [0, 2, -2, 0], rotate: [0, 3, -3, 0] },
  listening: { rotate: [0, -2, 2, 0] },
  celebrating_admission: { scale: [1, 1.12, 1], y: [0, -6, 0] },
  travel: { x: [0, 4, -4, 0] },
  tutor: { y: [0, -2, 0], rotate: [0, -1.5, 1.5, 0] },
  counsellor: { rotate: [0, -2, 2, 0] },
  ai_search: { x: [0, 3, -3, 0] },
  waiting: { rotate: [-4, 4, -4] },

  idle: { y: [0, -3, 0], rotate: [0, -1.5, 1.5, 0] },
  reply: { scale: [1, 1.12, 1], rotate: [0, -5, 5, 0] },
  greet: { rotate: [0, -15, 15, -10, 10, 0], y: [0, -5, 0] },
  wow: { scale: [1, 1.12, 1], y: [0, -5, 0] },
  wink: { rotate: [0, -3, 0] },
  idea: { scale: [1, 1.08, 1] },
  searching: { x: [0, 3, -3, 0] },
  reading: { y: [0, -2, 0] },
  talking: { y: [0, -2, 0] },
};

// Suggested idle expressions to rotate through when the user is idle.
export const IDLE_EXPRESSIONS = ["happy", "studying", "listening", "thinking"];

/**
 * Pick the most fitting expression for a chat message.
 * Maps to the 15 canonical states based on keyword match in the message.
 */
export function pickEmotion(text, role) {
  if (!text) return role === "user" ? "listening" : "happy";
  const t = text.toLowerCase();

  if (role === "user") {
    if (/^(hi|hello|hey|namaste|hola|good (morning|evening|afternoon))/.test(t))
      return "happy";
    if (/(wow|amazing|really\?|seriously|no way|omg)/.test(t)) return "surprised";
    if (/(confused|don't know|not sure|stuck|help me|lost|clueless)/.test(t))
      return "confused";
    if (/(admission|admitted|got in|accepted|offer letter|scholarship)/.test(t))
      return "celebrating_admission";
    if (/(travel|abroad|overseas|visa|passport|study (in|abroad)|usa|uk|canada|australia)/.test(t))
      return "travel";
    if (/(tutor|teach|lesson|class|subject|maths?|science|english|jee|neet|cbse|icse|board exam)/.test(t))
      return "tutor";
    if (/(counsel|career|stream|psychometric|dmit|guidance|mentor)/.test(t))
      return "counsellor";
    if (/(search|find|look(ing)? for|where can i|show me)/.test(t)) return "ai_search";
    if (/(think|wonder|consider|maybe|should i)/.test(t)) return "thinking";
    if (/(study|learn|revise|practice|prepare)/.test(t)) return "studying";
    if (/(sleep|tired|exhausted|bedtime|nap)/.test(t)) return "sleeping";
    if (/(wait|waiting|hold on|one sec|loading)/.test(t)) return "waiting";
    if (/(typ|writ|message|chat|talk)/.test(t)) return "typing";
    return "listening";
  }

  // Assistant reaction
  if (/(congrat|admitted|got in|admission|you('| a)?re in)/.test(t))
    return "celebrating_admission";
  if (/(great|awesome|fantastic|love that|excellent|brilliant|well done)/.test(t))
    return "cheering";
  if (/(abroad|overseas|visa|passport|study abroad|usa|uk|canada|australia)/.test(t))
    return "travel";
  if (/(tutor|tuition|teach|lesson|class|syllabus|maths?|science|english|jee|neet)/.test(t))
    return "tutor";
  if (/(counsell?or|counsell?ing|career path|stream|psychometric|dmit)/.test(t))
    return "counsellor";
  if (/(search|look(ing)? up|let me find|i('| wi)?ll check|exploring|browse|find)/.test(t))
    return "ai_search";
  if (/(think|consider|reflect|weigh|evaluate|let me think)/.test(t)) return "thinking";
  if (/(study|learn|practice|prepare|coaching|revise|read|book|article|sop|essay|application)/.test(t))
    return "studying";
  if (/(wow|whoa|surpris|unexpected|amazing)/.test(t)) return "surprised";
  if (/(wait|one sec|just a moment|loading|fetching)/.test(t)) return "waiting";
  return "happy";
}
